import { useMemo } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { parseQueryString } from '@/utils/queryString';
import { normalizeReferringIpPrefixLength } from '../utils/referringIpSubnet';
import useDomain from '@/hooks/useDomain';

type FilterColumn = {
  operator?: string;
  title?: string;
  filterModes?: { mode?: string; value?: unknown; key?: number }[];
};

/**
 * Drop UI-only filter keys before they reach RTK Query.
 * `parseQueryString` keys are for chip edit/delete; including them in the
 * request body changes the cache key on every render while a filter is active.
 */
export function stableBacklinkFilters(
  filters: Record<string, FilterColumn> | null | undefined
): Record<string, Omit<FilterColumn, 'filterModes'> & {
  filterModes: { mode?: string; value?: unknown }[];
}> {
  if (!filters || typeof filters !== 'object') return {};

  return Object.fromEntries(
    Object.entries(filters).map(([column, value]) => [
      column,
      {
        operator: value?.operator,
        title: value?.title ?? '',
        filterModes: (value?.filterModes ?? []).map(
          ({ mode, value: modeValue }) => ({
            mode,
            value: modeValue
          })
        )
      }
    ])
  );
}

export const toBacklinkUrl = (domainName?: string | null): string =>
  domainName?.startsWith('http') ? domainName : `https://${domainName}`;

const VALID_BACKLINK_GROUPINGS = new Set(['domain', 'all']);

export const normalizeBacklinkGrouping = (raw: unknown): string =>
  VALID_BACKLINK_GROUPINGS.has(String(raw ?? '').trim())
    ? String(raw).trim()
    : 'domain';

type OverviewQueryInput = {
  domain_slug?: string | null;
  grouping?: string;
  filters?: Record<string, unknown>;
};

/**
 * Shared product API body — scope is `domain_slug` (url/url_type resolved on hub).
 * `url` is only for first-time analyze (from path domain).
 */
export const toOverviewQueryBody = ({
  domain_slug,
  grouping,
  filters
}: OverviewQueryInput) => ({
  domain_slug,
  grouping,
  filters
});

const useBacklinkQueryBody = () => {
  const [searchParams] = useSearchParams();
  const { domain, domain_slug: paramDomainSlug } = useParams();
  const { domain_slug: contextDomainSlug } = useDomain();
  const search = searchParams.toString();

  return useMemo(() => {
    const params = new URLSearchParams(search);
    const domain_slug = paramDomainSlug || contextDomainSlug || null;
    /** Path domain → https URL; used for analyze before a product_mv_task exists. */
    const url = domain ? toBacklinkUrl(domain) : null;
    const grouping = normalizeBacklinkGrouping(params.get('grouping'));
    const ip_prefix_length = normalizeReferringIpPrefixLength(
      params.get('ip_prefix')
    );
    const filters = stableBacklinkFilters(
      parseQueryString(`?${search}`) as Record<string, FilterColumn>
    );

    return { url, domain_slug, grouping, ip_prefix_length, filters };
  }, [search, domain, paramDomainSlug, contextDomainSlug]);
};

export default useBacklinkQueryBody;
